import React from 'react';
import { Sparkles } from 'lucide-react';
import ProposalCard from './ProposalCard';
import './ai.css';

/**
 * The one way AI-proposed changes are shown and approved, wherever they come
 * from: a note, the assistant, a nudge, the executive review. Each item is
 * accepted or rejected on its own; one button confirms the accepted ones.
 * The stack never writes — `onConfirm` does, through the ordinary routes.
 *
 * items: [{ id, kind, title, description?, changes?: [{label, from?, to}], reason?, accepted?, editable?, multiline?,
 *          fields?: [{ key, label, value, type?, multiline?, emptyText? }], edited? }]
 * onFieldEdit(id, key, value): fix one field in place; throw to reject it.
 */
export default function ProposalStack({
  title,
  intro,
  items,
  onToggle,
  onEdit,
  onFieldEdit,
  onConfirm,
  onDismiss,
  confirmLabel = 'Save',
  dismissLabel = 'Cancel',
  showCount = false,
  busy = false,
  ariaLabel,
  role = 'dialog',
  children,
  footer,
  className = '',
}) {
  const acceptedCount = items.filter((i) => i.accepted !== false).length;
  const label = showCount && items.length > 1 && acceptedCount > 0 ? `${confirmLabel} (${acceptedCount})` : confirmLabel;
  const canConfirm = !!onConfirm && !busy && acceptedCount > 0;

  // ⌘↵ / Ctrl+Enter anywhere in the stack confirms, except while a field is
  // being edited, where the same keys save that field.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey) || !canConfirm) return;
    const tag = e.target && e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    e.preventDefault();
    onConfirm();
  };

  return (
    <section className={`ai-proposal ${className}`} role={role} aria-label={ariaLabel || title} onKeyDown={onKeyDown}>
      <header className="ai-proposal-header">
        <h4 className="ai-proposal-title"><Sparkles size={14} aria-hidden="true" /> {title}</h4>
        {intro && <p className="ai-proposal-intro">{intro}</p>}
      </header>
      {items.length > 0 ? (
        <ul className="ai-proposal-list">
          {items.map((item) => (
            <li key={item.id}>
              <ProposalCard item={item} onToggle={onToggle} onEdit={onEdit} onFieldEdit={onFieldEdit} disabled={busy} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="ai-proposal-intro">Nothing to change.</p>
      )}
      {children}
      {(onConfirm || onDismiss || footer) && (
        <footer className="ai-proposal-actions">
          {onConfirm && <button type="button" className="btn btn-primary btn-sm" onClick={onConfirm} disabled={!canConfirm} aria-keyshortcuts={onConfirm ? 'Control+Enter Meta+Enter' : undefined}>{label}</button>}
          {onDismiss && <button type="button" className="btn btn-secondary btn-sm" onClick={onDismiss} disabled={busy}>{dismissLabel}</button>}
          {footer}
        </footer>
      )}
    </section>
  );
}
